/**
 * Attachments on a support ticket, as the admin modal needs them.
 *
 * Tickets filed from the user-app dashboard carry their files in
 * `diagnostics.attachments` — one `{ name, type, size, url }` per file, where
 * `type` is the MIME type, `size` is bytes and `url` is a signed absolute link
 * (null when the user-app had no secret to sign with). Nothing else about the
 * files is stored on the ticket row, and replies carry no attachments.
 *
 * Kept free of React so the parsing and classification are unit-testable.
 */

export type AttachmentKind = 'image' | 'video' | 'other';

export interface TicketAttachment {
  name: string;
  /** MIME type as filed; '' when absent. */
  type: string;
  /** Bytes, or null when not a usable number. */
  size: number | null;
  /** Always an http(s) URL — anything else is dropped before it gets here. */
  url: string;
  kind: AttachmentKind;
}

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp', 'svg']);
const VIDEO_EXT = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogv']);

/** The file extension of a name, lower-cased, without any query/hash. */
function extensionOf(s: string): string {
  const clean = s.split(/[?#]/)[0];
  const dot = clean.lastIndexOf('.');
  const slash = Math.max(clean.lastIndexOf('/'), clean.lastIndexOf('\\'));
  return dot > slash ? clean.slice(dot + 1).toLowerCase() : '';
}

/** MIME type first; the file name's extension only when the type says nothing useful. */
export function attachmentKind(type: string, name: string): AttachmentKind {
  const t = (type || '').trim().toLowerCase();
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t && t !== 'application/octet-stream') return 'other';
  const ext = extensionOf(name || '');
  if (IMAGE_EXT.has(ext)) return 'image';
  if (VIDEO_EXT.has(ext)) return 'video';
  return 'other';
}

/** Only http/https — never javascript:, data:, blob: and friends. */
export function isSafeHttpUrl(u: unknown): u is string {
  if (typeof u !== 'string') return false;
  try {
    const p = new URL(u.trim()).protocol;
    return p === 'http:' || p === 'https:';
  } catch {
    return false;
  }
}

/** "1.4 MB" — 1024 steps, like a file manager. '' for an unusable size. */
export function formatBytes(n: number | null | undefined): string {
  if (typeof n !== 'number' || !isFinite(n) || n < 0) return '';
  if (n < 1024) return `${Math.round(n)} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v >= 10 ? Math.round(v) : Math.round(v * 10) / 10} ${units[i]}`;
}

/**
 * Attachments from a ticket's `diagnostics`, which arrives as an object or as
 * a JSON string. A malformed string, a missing/non-array `attachments`, or an
 * entry with no http(s) `url` yields nothing for that part — never a throw.
 */
export function parseTicketAttachments(diagnostics: unknown): TicketAttachment[] {
  let d: unknown = diagnostics;
  if (typeof d === 'string') {
    try { d = JSON.parse(d); } catch { return []; }
  }
  if (!d || typeof d !== 'object') return [];
  const list = (d as { attachments?: unknown }).attachments;
  if (!Array.isArray(list)) return [];

  const out: TicketAttachment[] = [];
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const a = raw as Record<string, unknown>;
    if (!isSafeHttpUrl(a.url)) continue;
    const url = (a.url as string).trim();
    const name = typeof a.name === 'string' && a.name.trim() ? a.name.trim() : 'attachment';
    const type = typeof a.type === 'string' ? a.type : '';
    const size = typeof a.size === 'number' && isFinite(a.size) && a.size >= 0 ? a.size : null;
    out.push({ name, type, size, url, kind: attachmentKind(type, name) });
  }
  return out;
}

/* ------------------------------------------------------------------------- *
 * Files the BACKEND vouches for.
 *
 * `GET /tickets/admin/:id` returns `attachments: [{ id, name, type, size }]`,
 * and each file's bytes are served, to a signed-in admin only, from
 * `GET /tickets/admin/:id/attachments/:attachmentId`. There is no URL in
 * this shape on purpose: the admin fetches the bytes with its own session and
 * shows them from a blob: URL. Everything above (diagnostics.attachments) is
 * client-supplied JSON and stays only as the fallback for an older backend.
 * ------------------------------------------------------------------------- */

export interface StoredAttachment {
  id: string;
  name: string;
  type: string;
  size: number | null;
  kind: AttachmentKind;
}

/** Images larger than this wait for a click instead of loading on their own. */
export const IMAGE_AUTOLOAD_MAX_BYTES = 8 * 1024 * 1024;
/** Videos are held in memory to play, so past this they are download-only. */
export const VIDEO_INLINE_MAX_BYTES = 100 * 1024 * 1024;

/** Raster types that are safe to open in their own tab from a blob: URL. SVG is
 *  not on the list: a top-level SVG runs script with this page's origin. */
const OPENABLE_IMAGE_TYPES = new Set([
  'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif', 'image/bmp',
]);
export function isOpenableImageType(type: string): boolean {
  return OPENABLE_IMAGE_TYPES.has((type || '').split(';')[0].trim().toLowerCase());
}

/**
 * `attachments` as the backend sent it. `null` means the backend did not send
 * the field at all (an older backend), so the caller falls back to
 * diagnostics; an array — even an empty one — is the backend's final word.
 * Entries with no usable id are dropped.
 */
export function parseStoredAttachments(raw: unknown): StoredAttachment[] | null {
  if (!Array.isArray(raw)) return null;
  const out: StoredAttachment[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const a = item as Record<string, unknown>;
    const id = typeof a.id === 'string' ? a.id.trim()
      : typeof a.id === 'number' && isFinite(a.id) ? String(a.id) : '';
    if (!id) continue;
    const name = typeof a.name === 'string' && a.name.trim() ? a.name.trim() : 'attachment';
    const type = typeof a.type === 'string' ? a.type : '';
    const size = typeof a.size === 'number' && isFinite(a.size) && a.size >= 0 ? a.size : null;
    out.push({ id, name, type, size, kind: attachmentKind(type, name) });
  }
  return out;
}

/** Backend-relative path (for backendApi) of one attachment's bytes. */
export function attachmentBytesPath(ticketId: string, attachmentId: string): string {
  return `/tickets/admin/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`;
}

/** `attachment_count` from a list row: a whole number >= 1, else 0 (absent, null, junk). */
export function attachmentCount(v: unknown): number {
  return typeof v === 'number' && isFinite(v) && v >= 1 ? Math.floor(v) : 0;
}
